import React, { useEffect, useState } from 'react';
import './NewsBox.css';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import pgLogo from '../../assets/pglogo.png';

const BOARD_ID = process.env.REACT_APP_MONDAY_BOARD_ID;
const TOKEN = process.env.REACT_APP_MONDAY_TOKEN;

const DATE_COLUMN_ID = 'date4';                // Publishing Date
const EVENT_DATE_COLUMN_ID = 'date_mm604p0a';  // Event Date
const HOUR_COLUMN_ID = 'hour_mm63tb59';        // Hour
const LOCATION_COLUMN_ID = 'text_mm633e3y';    // Location
const NEWS_COLUMN_ID = 'text_mm8ac6v4';        // News body text
const EVENT_COLUMN_ID = 'text_mm8acn1z';       // Event body text
const ATTACHMENT_COLUMN_ID = 'file_mm4xhpsg';  // Image/file attachment

const REFRESH_INTERVAL = 30000;

export const fetchNews = async (limit = Infinity) => {
  const query = `
    {
      boards(ids: ${BOARD_ID}) {
        items_page(limit: 100) {
          items {
            id
            name
            column_values {
              id
              text
              ... on FileValue {
                files {
                  ... on FileAssetValue {
                    asset {
                      id
                      name
                      public_url
                      file_extension
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const res = await fetch('https://api.monday.com/v2', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': TOKEN,
    },
    body: JSON.stringify({ query }),
  });

  const data = await res.json();
  const items = data?.data?.boards?.[0]?.items_page?.items ?? [];

  return items
    .map(item => {
      const getCol = (id) => item.column_values.find(c => c.id === id);
      const getColText = (id) => getCol(id)?.text ?? '';

      const newsText = getColText(NEWS_COLUMN_ID);
      const eventText = getColText(EVENT_COLUMN_ID);
      // An item is an "event" if the Event column has content and News doesn't.
      const type = eventText.trim() && !newsText.trim() ? 'event' : 'news';

      // Pull image URLs from the FileValue's asset.public_url — a signed,
      // session-independent URL — instead of the column's plain .text
      // (which returns a monday.com-session-cookie-protected link and
      // breaks for anyone not actively logged into monday.com).
      const attachmentCol = getCol(ATTACHMENT_COLUMN_ID);
      const images = (attachmentCol?.files ?? [])
        .map((f) => f.asset?.public_url)
        .filter(Boolean);

      return {
        id: item.id,
        title: item.name,
        date: getColText(DATE_COLUMN_ID),
        eventDate: getColText(EVENT_DATE_COLUMN_ID),
        hour: getColText(HOUR_COLUMN_ID),
        location: getColText(LOCATION_COLUMN_ID),
        body: newsText,       // kept for backward compat with the homepage NewsBox widget
        eventText,
        type,
        image: images[0] || null,
        images,
      };
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, limit); // ✅ limits based on caller
};

const NewsBox = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const loadNews = () => {
    fetchNews(3) // ✅ only 3 on homepage
      .then(data => {
        setNews(data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadNews();
    const interval = setInterval(loadNews, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="news-box">
      <div className="news-box-header">
        <div className="news-header-left">
          <h3>Latest News</h3>
        </div>
        <button className="news-read-more" onClick={() => navigate('/news')}>
          Read More
        </button>
      </div>
      <div className="news-list">
        {loading && <p className="news-status">Loading...</p>}
        {error && <p className="news-status">Failed to load news.</p>}
        {!loading && !error && news.length === 0 && (
          <p className="news-status">No news available.</p>
        )}
        <AnimatePresence>
          {news.map(item => (
            <motion.div
              className="news-item"
              key={item.id}
              initial={{ opacity: 0, filter: 'blur(6px)', y: -6 }}
              animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
              exit={{ opacity: 0, filter: 'blur(6px)', y: -6 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              <img
                src={item.image || pgLogo}
                alt={item.title}
                className={`news-item-thumb${item.image ? '' : ' news-item-thumb--fallback'}`}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = pgLogo;
                  e.currentTarget.classList.add('news-item-thumb--fallback');
                }}
              />
              <div className="news-item-content">
                <span className="news-date">{item.date}</span>
                <p className="news-title">{item.title}</p>
                <p className="news-body">{item.body}</p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default NewsBox;